/**
 * 辩手「我的位置」情境条：
 * - 自己在哪个阵营/辩位、队友与对手是谁；
 * - 当前环节、已完成进度、下一环节；
 * - 当前/已激活的 DAG 节点；
 * - 面向自己位置与阶段的准备提醒。
 */
import { useMemo } from 'react';
import {
  AI_GLASSES_MOTION,
  SEATS,
  sideLabel,
  type Seat,
  type Side,
} from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';

const SEAT_LABEL: Record<Seat, string> = { 1: '一辩', 2: '二辩', 3: '三辩', 4: '四辩' };

function ownRoleLabel(side: Side, seat?: number): string {
  const label = seat && seat >= 1 && seat <= 4 ? SEAT_LABEL[seat as Seat] : '观察';
  return `${sideLabel(side)} · ${label}`;
}

export function SituationStrip() {
  const { state } = useArena();
  const self = state.self;
  const selfSide: Side = self?.side ?? 'AFF';
  const opponentSide: Side = selfSide === 'AFF' ? 'NEG' : 'AFF';
  const stage = state.stage;

  const team = useMemo(
    () =>
      state.participants
        .filter((participant) => participant.role === 'DEBATER' && participant.side === selfSide)
        .sort((a, b) => (a.seat ?? 0) - (b.seat ?? 0)),
    [state.participants, selfSide],
  );
  const opponents = useMemo(
    () =>
      state.participants
        .filter((participant) => participant.role === 'DEBATER' && participant.side === opponentSide)
        .sort((a, b) => (a.seat ?? 0) - (b.seat ?? 0)),
    [state.participants, opponentSide],
  );

  const activeNodes = useMemo(() => {
    const reached = new Set<number>([...state.completedStages, state.stageIndex].filter((n) => n > 0));
    if (reached.size === 0) return [];
    return AI_GLASSES_MOTION.dag.nodes
      .filter(
        (node) =>
          node.side === selfSide &&
          [...reached].some(
            (stageIndex) => node.stageRange[0] <= stageIndex && stageIndex <= node.stageRange[1],
          ),
      )
      .slice(-3)
      .reverse();
  }, [state.completedStages, state.stageIndex, selfSide]);

  const speakingNow =
    stage && stage.speakerSide === selfSide && stage.speakerRole === ownRoleLabel(selfSide, self?.seat);

  const reminder = (() => {
    if (state.phase === 'LOBBY') return '等待教师开赛；先确认自己的座位与队形。';
    if (!stage) return '等待下一环节。';
    if (state.stagePhase === 'PREP') {
      return speakingNow
        ? `读卡期：先看核心任务与必打论据，${Math.ceil(state.prepRemainingMs / 1000)}s 后开表。`
        : '读卡期：记录对方可能的流派，准备质询切入点。';
    }
    if (speakingNow) return '你在发言：主卡三段 + 必打论据，先锁定义再展开。';
    if (stage.speakerSide === selfSide) return '队友在发言：盯对方口径，准备质询补位。';
    return '对方发言：记录数据口径，准备下一轮反击。';
  })();

  return (
    <section className="lens grid gap-4 p-3 lg:grid-cols-[1.15fr_1fr_1.15fr] xl:grid-cols-[1.2fr_.9fr_1fr_1.2fr]" data-testid="hud-situation">
      <div className="min-w-0">
        <div className="micro-label">MY POSITION</div>
        <div className="mt-1 flex items-center gap-2" data-testid="hud-own-position">
          <span className="text-[15px] font-semibold text-slate-100">
            {self ? ownRoleLabel(selfSide, self.seat) : '未入座'}
          </span>
          <span className="pill">{self?.name ?? '等待加入'}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1" data-testid="hud-teammates">
          {SEATS.map((seat) => {
            const member = team.find((participant) => participant.seat === seat);
            const isSelf = member?.id === self?.id;
            return (
              <span
                key={seat}
                className={`rounded px-1.5 py-1 text-[9.5px] ${
                  isSelf
                    ? 'bg-white/10 text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
                    : member
                      ? 'bg-white/[0.04] text-slate-300'
                      : 'bg-white/[0.02] text-slate-600'
                }`}
                title={member?.name ?? '空位'}
              >
                {SEAT_LABEL[seat]} · {member ? member.name.slice(0, 4) : '空'}
              </span>
            );
          })}
        </div>
        <div className="mt-1 text-[9px] text-slate-500">
          对手：{opponents.length > 0 ? opponents.map((p) => p.name).join(' / ') : '等待对方加入'}
        </div>
      </div>

      <div className="min-w-0">
        <div className="micro-label">STAGE / PROGRESS</div>
        <div className="mt-1 text-[13px] font-medium text-slate-100">
          {stage ? `环节 ${stage.index}/16 · ${stage.name}` : '尚未开始'}
        </div>
        <div className="mt-2 flex gap-[3px]">
          {Array.from({ length: 16 }, (_, index) => {
            const stageNumber = index + 1;
            const done = state.completedStages.includes(stageNumber);
            const current = state.stageIndex === stageNumber;
            return (
              <span
                key={stageNumber}
                className={`h-1.5 flex-1 rounded-full ${
                  current
                    ? 'bg-slate-100 shadow-[0_0_8px_rgba(255,255,255,0.45)]'
                    : done
                      ? 'bg-white/45'
                      : 'bg-white/10'
                }`}
              />
            );
          })}
        </div>
        <div className="mt-1 text-[9px] text-slate-500">
          {state.stagePhase === 'PREP'
            ? `读卡 ${Math.ceil(state.prepRemainingMs / 1000)}s`
            : `${state.completedStages.length}/16 已完成`}
          {stage && stage.index < 16 ? ` · 下一环节：${stage.index + 1}` : ''}
        </div>
      </div>

      <div className="min-w-0">
        <div className="micro-label">PREP REMINDER</div>
        <div className="mt-1 flex items-start gap-2" data-testid="hud-prep-reminder">
          <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#FCD34D] shadow-[0_0_8px_rgba(252,211,77,0.8)]" />
          <p className="text-[10.5px] leading-relaxed text-slate-300">{reminder}</p>
        </div>
        {stage && (
          <div className="mt-1 text-[9px] text-slate-500">
            {speakingNow ? '你是本环节发言方' : '本环节发言方：' + stage.speakerRole}
          </div>
        )}
      </div>

      <div className="min-w-0">
        <div className="micro-label">DAG NODES · 已触达</div>
        <div className="mt-1 flex flex-wrap gap-1" data-testid="hud-visited-nodes">
          {activeNodes.length === 0 ? (
            <span className="text-[10px] text-slate-500">尚未触达节点；开赛后按环节点亮。</span>
          ) : (
            activeNodes.map((node) => (
              <span key={node.id} className="rounded bg-white/[0.045] px-1.5 py-1 text-[9.5px] text-slate-300" title={node.summary}>
                {node.label}
              </span>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
